import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SELFCHECK_QUESTIONS, SELFCHECK_OPTIONS, ORG_TYPES } from "@/data/catalogue";
import { api, formatApiError } from "@/lib/api";

const STEP = { INTRO: "intro", QUESTIONS: "questions", GATE: "gate", RESULT: "result" };

export default function ReadinessCheck() {
  const [step, setStep] = useState(STEP.INTRO);
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", organisation: "", org_type: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const total = SELFCHECK_QUESTIONS.length;
  const answered = Object.keys(answers).length;

  const answer = (id, value) => {
    setAnswers((a) => ({ ...a, [id]: value }));
    setTimeout(() => {
      if (qIndex < total - 1) setQIndex((i) => i + 1);
      else setStep(STEP.GATE);
    }, 180);
  };

  const submit = async () => {
    if (!contact.name || !contact.email || !contact.org_type) {
      toast.error("Please complete name, email and organisation type.");
      return;
    }
    if (!contact.consent) {
      toast.error("Please provide consent to view your results.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/self-check", { ...contact, answers });
      setResult(data);
      setStep(STEP.RESULT);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not calculate results.");
    } finally {
      setLoading(false);
    }
  };

  const q = SELFCHECK_QUESTIONS[qIndex];

  return (
    <div className="min-h-screen bg-background">
      {/* Minimal top bar */}
      <div className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
          <Link to="/" className="font-display text-lg font-bold tracking-tight">BIGTEK</Link>
          <Link to="/catalogue" className="text-sm text-muted-foreground hover:text-foreground">
            Exit self-check
          </Link>
        </div>
        {step === STEP.QUESTIONS && (
          <Progress value={((qIndex + 1) / total) * 100} className="h-1 rounded-none" />
        )}
      </div>

      <div className="mx-auto max-w-3xl px-5 py-16 lg:py-24">
        {/* INTRO */}
        {step === STEP.INTRO && (
          <div className="animate-fade-up">
            <div className="text-xs uppercase tracking-widest-xs text-[hsl(var(--brand))]">
              Governance Readiness Self-Check
            </div>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              A 2-minute indicative check.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              Twelve questions across authority, consent, evidence, data lifecycle and oversight. You'll
              receive an indicative readiness band. This is a free teaser — <strong>it is not the paid
              Governance Readiness Assessment</strong> and produces no formal findings, certification or advice.
            </p>
            <div className="mt-8 border border-border bg-secondary/50 p-5 text-sm leading-relaxed text-muted-foreground">
              We ask for your email at the end so we can send your result. Your answers are stored only
              to generate and follow up on this indicative result — not sold, not used for AI training.
            </div>
            <Button
              data-testid="selfcheck-start"
              onClick={() => setStep(STEP.QUESTIONS)}
              className="mt-8 rounded-sm bg-[hsl(var(--brand))] py-6 text-white hover:bg-[hsl(217_91%_53%)] sm:px-10"
            >
              Begin <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} />
            </Button>
          </div>
        )}

        {/* QUESTIONS */}
        {step === STEP.QUESTIONS && q && (
          <div key={q.id} className="animate-fade-up">
            <div className="flex items-center justify-between text-xs uppercase tracking-widest-xs text-muted-foreground">
              <span>{q.dimension}</span>
              <span>{qIndex + 1} / {total}</span>
            </div>
            <h2 className="mt-6 font-display text-2xl font-medium leading-snug sm:text-3xl">
              {q.question}
            </h2>
            <div className="mt-10 space-y-3">
              {SELFCHECK_OPTIONS.map((opt, oi) => (
                <button
                  key={oi}
                  data-testid={`selfcheck-option-${qIndex}-${oi}`}
                  onClick={() => answer(q.id, opt.value)}
                  className={`flex w-full items-center justify-between border p-5 text-left text-base transition-colors duration-200 ${
                    answers[q.id] === opt.value && oi === SELFCHECK_OPTIONS.findIndex((o) => o.value === answers[q.id])
                      ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/5"
                      : "border-border hover:border-foreground hover:bg-secondary"
                  }`}
                >
                  {opt.label}
                  <ArrowRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                </button>
              ))}
            </div>
            <div className="mt-10 flex items-center justify-between">
              <Button
                variant="ghost"
                data-testid="selfcheck-back"
                disabled={qIndex === 0}
                onClick={() => setQIndex((i) => Math.max(0, i - 1))}
                className="rounded-sm"
              >
                <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} /> Back
              </Button>
            </div>
          </div>
        )}

        {/* GATE */}
        {step === STEP.GATE && (
          <div className="animate-fade-up">
            <div className="text-xs uppercase tracking-widest-xs text-[hsl(var(--brand))]">
              Almost done — {answered}/{total} answered
            </div>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight">
              Where should we send your result?
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              We'll display your indicative band and email you a copy.
            </p>
            <div className="mt-8 space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="sc-name">Full name *</Label>
                  <Input id="sc-name" data-testid="selfcheck-name" className="rounded-sm"
                    value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sc-email">Work email *</Label>
                  <Input id="sc-email" type="email" data-testid="selfcheck-email" className="rounded-sm"
                    value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="sc-org">Organisation</Label>
                  <Input id="sc-org" data-testid="selfcheck-org" className="rounded-sm"
                    value={contact.organisation} onChange={(e) => setContact({ ...contact, organisation: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Organisation type *</Label>
                  <Select value={contact.org_type} onValueChange={(v) => setContact({ ...contact, org_type: v })}>
                    <SelectTrigger data-testid="selfcheck-org-type" className="rounded-sm">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {ORG_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex cursor-pointer items-start gap-3 border border-border bg-secondary/50 p-4">
                <Checkbox data-testid="selfcheck-consent" checked={contact.consent}
                  onCheckedChange={(v) => setContact({ ...contact, consent: !!v })} className="mt-0.5" />
                <span className="text-xs leading-relaxed text-muted-foreground">
                  I consent to BIGTEK storing my answers and contact details to generate this indicative
                  result and follow up. Stored for up to 24 months, then deleted. Not sold, not used for
                  AI training.
                </span>
              </label>
              <Button data-testid="selfcheck-submit" onClick={submit} disabled={loading}
                className="rounded-sm bg-[hsl(var(--brand))] py-6 text-white hover:bg-[hsl(217_91%_53%)] sm:px-10">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Calculating…</> : "See my result"}
              </Button>
            </div>
          </div>
        )}

        {/* RESULT */}
        {step === STEP.RESULT && result && (
          <div className="animate-fade-up" data-testid="selfcheck-result">
            <div className="text-xs uppercase tracking-widest-xs text-[hsl(var(--brand))]">
              Indicative result
            </div>
            <div className="mt-6 border border-border">
              <div className="bg-[hsl(222_47%_7%)] p-8 text-white">
                <ShieldCheck className="h-8 w-8 text-[hsl(var(--brand))]" strokeWidth={1.5} />
                <div className="mt-4 font-display text-3xl font-bold">{result.band}</div>
                <div className="mt-2 font-mono text-sm text-slate-400">
                  Indicative readiness index: {result.percent}%
                </div>
                <div className="mt-4 h-2 w-full overflow-hidden bg-slate-800">
                  <div className="h-full bg-[hsl(var(--brand))]" style={{ width: `${result.percent}%` }} />
                </div>
              </div>
              <div className="p-8">
                <p className="text-base leading-relaxed text-muted-foreground">{result.summary}</p>
                <div className="mt-6 border-t border-border pt-6 text-xs leading-relaxed text-slate-500">
                  This indicative band is a lead-gen teaser only. It is not the paid Governance Readiness
                  Assessment, not certification, and not advice. A full Assessment produces evidenced
                  findings and a defensible baseline.
                </div>
              </div>
            </div>

            <div className="mt-8 border border-border bg-secondary/50 p-8">
              <h3 className="font-display text-xl font-medium">Take the next step</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Commission the full Governance Readiness Assessment (From $7,500 + GST) or book a
                90-minute executive briefing.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Button asChild data-testid="result-assessment-cta"
                  className="rounded-sm bg-[hsl(var(--brand))] text-white hover:bg-[hsl(217_91%_53%)]">
                  <Link to="/catalogue/governance-readiness-assessment#enquire">
                    Enquire about the Assessment <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} />
                  </Link>
                </Button>
                <Button asChild variant="outline" data-testid="result-booking-cta" className="rounded-sm">
                  <Link to="/book-briefing">
                    <Calendar className="mr-2 h-4 w-4" strokeWidth={1.5} /> Book a briefing
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
